/* eslint-disable */
/**
 * v-auto-tooltip — directiva Vue 2 que crea un tooltip a nivel de
 * <body> con auto-posicionamiento segun el viewport.
 *
 * Por que a nivel de body: el sidebar, drawer y tray tienen
 * overflow: hidden (necesario para sus bordes redondeados y
 * glassmorphism). Un tooltip CSS con position: absolute dentro
 * de esos contenedores quedaria CLIPADO al llegar a los bordes.
 * Un tooltip a nivel de body escapa de cualquier overflow y
 * siempre se ve completo.
 *
 * Uso:
 *   <v-btn v-auto-tooltip="'Abrir barra lateral'">...
 *   <v-btn v-auto-tooltip="tooltipText">...  (reactivo)
 *
 * Comportamiento:
 * - Al hover/focus: crea (o reutiliza) un div global en <body>,
 *   mide sus dimensiones, calcula la mejor posicion (top si hay
 *   espacio, sino bottom; clamp horizontal al viewport), lo
 *   posiciona con position: fixed.
 * - Al leave/blur: lo oculta con transicion.
 * - Al scroll/resize: lo oculta (la posicion quedaria stale).
 * - Al unmount del elemento: cleanup completo.
 *
 * Solo se usa UN elemento tooltip global (singleton) para evitar
 * crear/destruir DOM en cada hover.
 */

// Margenes y offsets
const VIEWPORT_MARGIN = 8 // min separacion entre tooltip y borde del viewport
const TARGET_OFFSET = 6    // separacion entre tooltip y elemento target

// Estado global
let tooltipEl = null
let currentTarget = null
let showTimeout = null

function ensureTooltipEl() {
  if (tooltipEl) return tooltipEl
  tooltipEl = document.createElement('div')
  tooltipEl.className = 'auto-tooltip'
  tooltipEl.setAttribute('role', 'tooltip')
  document.body.appendChild(tooltipEl)
  return tooltipEl
}

function computePosition(targetRect, tipRect) {
  const vw = window.innerWidth
  const vh = window.innerHeight

  const spaceTop = targetRect.top
  const spaceBottom = vh - targetRect.bottom
  const needVertical = tipRect.height + TARGET_OFFSET + VIEWPORT_MARGIN

  // Prioridad 1: arriba si hay espacio suficiente
  let placeTop = spaceTop >= needVertical
  // Si no, abajo
  if (!placeTop && spaceBottom >= needVertical) {
    placeTop = false
  } else if (!placeTop) {
    // No cabe ni arriba ni abajo — preferimos el lado con mas espacio
    placeTop = spaceTop >= spaceBottom
  }

  // Calcular top
  let top
  if (placeTop) {
    top = targetRect.top - tipRect.height - TARGET_OFFSET
  } else {
    top = targetRect.bottom + TARGET_OFFSET
  }
  // Clamp vertical por si no cabe en ningun lado
  if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN
  if (top + tipRect.height > vh - VIEWPORT_MARGIN) {
    top = vh - VIEWPORT_MARGIN - tipRect.height
  }

  // Calcular left centrado horizontalmente sobre el target
  let left = targetRect.left + targetRect.width / 2 - tipRect.width / 2
  // Clamp horizontal al viewport
  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
  if (left + tipRect.width > vw - VIEWPORT_MARGIN) {
    left = vw - VIEWPORT_MARGIN - tipRect.width
  }

  return { top: Math.round(top), left: Math.round(left), placeTop }
}

function showTooltip(targetEl, text) {
  if (!text || !targetEl) return

  const tip = ensureTooltipEl()
  tip.textContent = text

  // Hacer visible temporalmente para medir, pero sin flash
  tip.style.display = 'block'
  tip.style.visibility = 'hidden'
  tip.style.opacity = '0'
  tip.style.top = '0px'
  tip.style.left = '0px'
  tip.style.transform = 'none'

  // Forzar layout para obtener dimensiones reales
  // (accesar offsetHeight triggea reflow)
  /* eslint-disable-next-line no-unused-expressions */
  tip.offsetHeight

  const tipRect = tip.getBoundingClientRect()
  const targetRect = targetEl.getBoundingClientRect()

  // Si el target tiene width/height 0 (display: none, aun no montado),
  // no mostramos tooltip
  if (targetRect.width === 0 && targetRect.height === 0) {
    tip.style.display = 'none'
    return
  }

  const { top, left, placeTop } = computePosition(targetRect, tipRect)

  tip.style.top = top + 'px'
  tip.style.left = left + 'px'
  tip.setAttribute('data-pos', placeTop ? 'top' : 'bottom')
  tip.style.visibility = 'visible'
  tip.style.opacity = '1'

  currentTarget = targetEl
}

function hideTooltip() {
  currentTarget = null
  if (showTimeout) {
    clearTimeout(showTimeout)
    showTimeout = null
  }
  if (!tooltipEl) return
  tooltipEl.style.opacity = '0'
  // No hacemos display: none — dejamos que la transicion corra y
  // queda invisible. Se resetea en el proximo show.
}

function forceHideTooltip() {
  currentTarget = null
  if (showTimeout) {
    clearTimeout(showTimeout)
    showTimeout = null
  }
  if (tooltipEl) {
    tooltipEl.style.opacity = '0'
    tooltipEl.style.display = 'none'
  }
}

// ─── Event handlers ───

function onEnter(event) {
  const el = event.currentTarget
  const text = el && el._autoTooltipText
  if (!text) return
  showTooltip(el, text)
}

function onLeave() {
  hideTooltip()
}

// ─── Listeners globales (una sola vez) ───

let globalListenersBound = false

function bindGlobalListeners() {
  if (globalListenersBound) return
  // Al scrollear o resize, el target cambia de posicion y el tooltip
  // quedaria en una ubicacion stale — lo ocultamos directamente.
  window.addEventListener('scroll', forceHideTooltip, { passive: true, capture: true })
  window.addEventListener('resize', forceHideTooltip, { passive: true })
  globalListenersBound = true
}

export default {
  install(Vue) {
    bindGlobalListeners()

    Vue.directive('auto-tooltip', {
      bind(el, binding) {
        el._autoTooltipText = binding.value == null ? '' : String(binding.value)
        el.addEventListener('mouseenter', onEnter)
        el.addEventListener('mouseleave', onLeave)
        el.addEventListener('focus', onEnter)
        el.addEventListener('blur', onLeave)
        el.addEventListener('click', onLeave) // Al click se oculta
      },
      update(el, binding) {
        el._autoTooltipText = binding.value == null ? '' : String(binding.value)
        // Si este elemento esta siendo hovered, actualiza el texto y
        // recomputa posicion (el texto puede cambiar de tamano)
        if (currentTarget === el && el._autoTooltipText) {
          showTooltip(el, el._autoTooltipText)
        }
      },
      unbind(el) {
        el.removeEventListener('mouseenter', onEnter)
        el.removeEventListener('mouseleave', onLeave)
        el.removeEventListener('focus', onEnter)
        el.removeEventListener('blur', onLeave)
        el.removeEventListener('click', onLeave)
        if (currentTarget === el) forceHideTooltip()
        el._autoTooltipText = null
      },
    })
  },
}
